/**
=========================================================
* Material Dashboard 3 PRO React - v2.4.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-pro-react
* Copyright 2024 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

// @mui material components
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import CircularProgress from "@mui/material/CircularProgress";
import Button from "@mui/material/Button";

// Material Dashboard 3 PRO React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";

// Material Dashboard 3 PRO React examples
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";

import GradientLineChart from "examples/Charts/LineCharts/GradientLineChart";
import DataTable from "examples/Tables/DataTable";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";

// Data

import { useState, useEffect, useRef, useLayoutEffect } from "react";
import axios from "axios";
import { useParams, useNavigate } from "react-router-dom";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { API_HOHOLOK } from "utils";

function formatTanggalIndo(dateStr) {
  if (!dateStr) return "-";
  const bulan = [
    "Januari",
    "Februari",
    "Maret",
    "April",
    "Mei",
    "Juni",
    "Juli",
    "Agustus",
    "September",
    "Oktober",
    "November",
    "Desember",
  ];
  const d = new Date(dateStr);
  if (isNaN(d)) return "-";
  return `${d.getDate()} ${bulan[d.getMonth()]} ${d.getFullYear()}`;
}

function KurvaTargetRealisasiProyek() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [chartData, setChartData] = useState(null);
  const [proyekList, setProyekList] = useState([]);
  const [selectedProyek, setSelectedProyek] = useState(null);
  const [tahun, setTahun] = useState(2025);
  const [loading, setLoading] = useState(false);
  const [id_perusahaan, setIdPerusahaan] = useState(null);
  const [status, setStatus] = useState(null);
  const [loadingChart, setLoadingChart] = useState(false);
  const [weeklyTable, setWeeklyTable] = useState({ columns: [], rows: [] });
  const currentProyekRef = useRef(null);
  const abortControllerRef = useRef(null);

  useEffect(() => {
    const userStorage = localStorage.getItem("user");
    if (userStorage) {
      const user = JSON.parse(userStorage);
      if (user.status === "user") {
        setIdPerusahaan(user.id_perusahaan);
      }
      setStatus(user.status);
    }
  }, []);

  const fetchProyek = () => {
    setLoading(true);
    const url = `${API_HOHOLOK}proyek/${id}`;
    axios
      .get(url)
      .then((res) => {
        const data = res.data || [];

        // Set selectedProyek dengan format yang sesuai
        setSelectedProyek({
          label: data.nama_proyek,
          value: data.id_proyek,
          ...data,
        });

        setLoading(false);
      })
      .catch(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    if (id) {
      fetchProyek();
    }
  }, [id]);

  // Fetch daftar proyek untuk dropdown

  // Helper tanggal pendek dd/mm/yyyy
  const formatTanggalShort = (dateObj) => {
    const d = new Date(dateObj);
    if (isNaN(d)) return "-";
    const dd = String(d.getDate()).padStart(2, "0");
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const yyyy = d.getFullYear();
    return `${dd}/${mm}/${yyyy}`;
  };

  const addDays = (date, days) => {
    const d = new Date(date);
    d.setDate(d.getDate() + days);
    return d;
  };

  // Fetch data chart sesuai proyek & tahun
  useEffect(() => {
    // Cancel previous request if exists
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // Create new abort controller
    abortControllerRef.current = new AbortController();

    // Immediately reset state and set loading
    setChartData(null);
    setLoadingChart(true);

    // Update current proyek ref
    currentProyekRef.current = selectedProyek?.value;

    if (id) {
      // Ambil kurva S untuk target
      axios
        .get(`${API_HOHOLOK}proyek/kurvas/${id}`, {
          signal: abortControllerRef.current.signal,
        })
        .then((res) => {
          const pekerjaanUtama = res.data?.pekerjaan_utama || [];
          const kurvaS = res.data?.kurva_s || [];

          // Minggu unik
          const mingguList = [...new Set(kurvaS.map((k) => k.minggu_ke))].sort(
            (a, b) => a - b
          );

          // Target akumulatif per minggu
          const totPro = [];
          const labelGrap = [];
          let cumulativeTotal = 0;
          mingguList.forEach((minggu) => {
            let totalProgres = 0;
            pekerjaanUtama.forEach((utama) => {
              const k = kurvaS.find(
                (ks) =>
                  Number(ks.id_pekerjaan_utama) ===
                    Number(utama.id_pekerjaan_utama) &&
                  Number(ks.minggu_ke) === Number(minggu)
              );
              if (k) totalProgres += Number(k.progres_rencana) || 0;
            });
            cumulativeTotal += totalProgres;
            totPro.push(cumulativeTotal);
            labelGrap.push(`Minggu ${minggu}`);
          });

          // Ambil data realisasi mingguan
          return axios
            .get(
              `${API_HOHOLOK}proyek/realisasi-mingguan/${id}?tahun=${tahun}`,
              {
                signal: abortControllerRef.current.signal,
              }
            )
            .then((realisasiRes) => {
              const realOK =
                realisasiRes.data &&
                realisasiRes.data.success &&
                Array.isArray(realisasiRes.data.data);
              if (realOK) {
                const realisasiData = realisasiRes.data.data;
                const totalNilaiProyek = realisasiData.reduce(
                  (sum, item) => sum + (Number(item.akumulasi_nilai) || 0),
                  0
                );
                const realisasiMap = {};
                realisasiData.forEach((item) => {
                  const label = `Minggu ${item.target_minggu_ke}`;
                  if (!realisasiMap[label]) {
                    realisasiMap[label] = { realisasi: 0, nilai: 0 };
                  }
                  realisasiMap[label].realisasi +=
                    Number(item.realisasi_nilai) || 0;
                  realisasiMap[label].nilai +=
                    Number(item.akumulasi_nilai) || 0;
                });

                let cumulativeRealisasi = 0;
                const akumulasiRealisasiPersen = [];
                labelGrap.forEach((label) => {
                  const m = realisasiMap[label] || { realisasi: 0, nilai: 0 };
                  cumulativeRealisasi += m.realisasi;
                  const realisasiPersen =
                    totalNilaiProyek > 0
                      ? (cumulativeRealisasi / totalNilaiProyek) * 100
                      : 0;
                  akumulasiRealisasiPersen.push(realisasiPersen);
                });

                setChartData({
                  labels: labelGrap,
                  datasets: [
                    {
                      label: "Akumulasi Target (%)",
                      color: "info",
                      data: totPro,
                    },
                    {
                      label: "Akumulasi Realisasi (%)",
                      color: "success",
                      data: akumulasiRealisasiPersen,
                    },
                  ],
                });

                // Build tabel mingguan (akumulatif), highlight minggu berjalan, panah deviasi
                const availableWeeks = Object.keys(realisasiMap).map((l) =>
                  Number((l || "").toString().replace(/[^0-9]/g, ""))
                );
                const maxWeekWithData =
                  availableWeeks.length > 0 ? Math.max(...availableWeeks) : -1;
                const lastWeekIdx = labelGrap.findIndex(
                  (l) => l === `Minggu ${maxWeekWithData}`
                );

                const projectStart = selectedProyek?.tanggal_mulai
                  ? new Date(selectedProyek.tanggal_mulai)
                  : null;
                const projectEnd = selectedProyek?.tanggal_selesai
                  ? new Date(selectedProyek.tanggal_selesai)
                  : null;

                let highlightedIdx = -1;
                if (projectStart instanceof Date && !isNaN(projectStart)) {
                  const today = new Date();
                  const msDiff = today.getTime() - projectStart.getTime();
                  const weekIdx = Math.floor(
                    msDiff / (1000 * 60 * 60 * 24 * 7)
                  );
                  const lastIdx = labelGrap.length - 1;
                  if (weekIdx < 0) highlightedIdx = 0;
                  else highlightedIdx = weekIdx > lastIdx ? lastIdx : weekIdx;
                }

                const weeklyRows = labelGrap.map((label, idx) => {
                  const targetCumulative = totPro[idx] || 0;
                  const hasRealData = lastWeekIdx >= 0 && idx <= lastWeekIdx;
                  const realCumulative = hasRealData
                    ? akumulasiRealisasiPersen[idx] || 0
                    : null;
                  const deviasi = hasRealData
                    ? (realCumulative || 0) - targetCumulative
                    : null;

                  let tanggalAwal = "-";
                  let tanggalAkhir = "-";
                  if (projectStart instanceof Date && !isNaN(projectStart)) {
                    const start = addDays(projectStart, idx * 7);
                    const end = addDays(projectStart, idx * 7 + 6);
                    const endCapped =
                      projectEnd && end > projectEnd ? projectEnd : end;
                    tanggalAwal = formatTanggalShort(start);
                    tanggalAkhir = formatTanggalShort(endCapped);
                  }

                  const deviasiCell =
                    deviasi === null ? (
                      "-"
                    ) : (
                      <span
                        style={{
                          color: deviasi < 0 ? "#d32f2f" : "#2e7d32",
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 4,
                          fontWeight: 600,
                        }}
                      >
                        {deviasi < 0 ? (
                          <ArrowDownwardIcon sx={{ fontSize: 16 }} />
                        ) : (
                          <ArrowUpwardIcon sx={{ fontSize: 16 }} />
                        )}
                        {`${deviasi.toFixed(2)}%`}
                      </span>
                    );

                  const mingguCell =
                    highlightedIdx === idx ? (
                      <span style={{ color: "#1a73e8", fontWeight: 700 }}>
                        {label}
                      </span>
                    ) : (
                      label
                    );

                  const rentangText = `${tanggalAwal} - ${tanggalAkhir}`;
                  const targetText = `${targetCumulative.toFixed(2)}%`;
                  const realisasiText =
                    realCumulative === null
                      ? "-"
                      : `${realCumulative.toFixed(2)}%`;
                  const toBlue = (content) => (
                    <span style={{ color: "#1a73e8", fontWeight: 600 }}>
                      {content}
                    </span>
                  );

                  return {
                    minggu: mingguCell,
                    rentang:
                      highlightedIdx === idx
                        ? toBlue(rentangText)
                        : rentangText,
                    target:
                      highlightedIdx === idx ? toBlue(targetText) : targetText,
                    realisasi:
                      highlightedIdx === idx
                        ? toBlue(realisasiText)
                        : realisasiText,
                    deviasi: deviasiCell,
                  };
                });

                setWeeklyTable({
                  columns: [
                    { Header: "Minggu", accessor: "minggu", width: "20%" },
                    {
                      Header: "Tanggal (Awal - Akhir)",
                      accessor: "rentang",
                      width: "30%",
                    },
                    { Header: "Target (%)", accessor: "target", width: "16%" },
                    {
                      Header: "Realisasi (%)",
                      accessor: "realisasi",
                      width: "16%",
                    },
                    {
                      Header: "Deviasi (%)",
                      accessor: "deviasi",
                      width: "18%",
                    },
                  ],
                  rows: weeklyRows,
                });
              } else {
                // Hanya target
                setChartData({
                  labels: labelGrap,
                  datasets: [
                    {
                      label: "Akumulasi Target (%)",
                      color: "info",
                      data: totPro,
                    },
                  ],
                });

                const projectStart = selectedProyek?.tanggal_mulai
                  ? new Date(selectedProyek.tanggal_mulai)
                  : null;
                const projectEnd = selectedProyek?.tanggal_selesai
                  ? new Date(selectedProyek.tanggal_selesai)
                  : null;
                let highlightedIdx = -1;
                if (projectStart instanceof Date && !isNaN(projectStart)) {
                  const today = new Date();
                  const msDiff = today.getTime() - projectStart.getTime();
                  const weekIdx = Math.floor(
                    msDiff / (1000 * 60 * 60 * 24 * 7)
                  );
                  const lastIdx = labelGrap.length - 1;
                  if (weekIdx < 0) highlightedIdx = 0;
                  else highlightedIdx = weekIdx > lastIdx ? lastIdx : weekIdx;
                }
                const weeklyRows = labelGrap.map((label, idx) => {
                  const targetCumulative = totPro[idx] || 0;
                  let tanggalAwal = "-";
                  let tanggalAkhir = "-";
                  if (projectStart instanceof Date && !isNaN(projectStart)) {
                    const start = addDays(projectStart, idx * 7);
                    const end = addDays(projectStart, idx * 7 + 6);
                    const endCapped =
                      projectEnd && end > projectEnd ? projectEnd : end;
                    tanggalAwal = formatTanggalShort(start);
                    tanggalAkhir = formatTanggalShort(endCapped);
                  }
                  const deviasi = 0 - targetCumulative;
                  const deviasiCell = (
                    <span
                      style={{
                        color: deviasi < 0 ? "#d32f2f" : "#2e7d32",
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 4,
                        fontWeight: 600,
                      }}
                    >
                      {deviasi < 0 ? (
                        <ArrowDownwardIcon sx={{ fontSize: 16 }} />
                      ) : (
                        <ArrowUpwardIcon sx={{ fontSize: 16 }} />
                      )}
                      {`${deviasi.toFixed(2)}%`}
                    </span>
                  );
                  const mingguCell =
                    highlightedIdx === idx ? (
                      <span style={{ color: "#1a73e8", fontWeight: 700 }}>
                        {label}
                      </span>
                    ) : (
                      label
                    );
                  const rentangText = `${tanggalAwal} - ${tanggalAkhir}`;
                  const targetText = `${targetCumulative.toFixed(2)}%`;
                  const realisasiText = `0.00%`;
                  const toBlue = (content) => (
                    <span style={{ color: "#1a73e8", fontWeight: 600 }}>
                      {content}
                    </span>
                  );
                  return {
                    minggu: mingguCell,
                    rentang:
                      highlightedIdx === idx
                        ? toBlue(rentangText)
                        : rentangText,
                    target:
                      highlightedIdx === idx ? toBlue(targetText) : targetText,
                    realisasi:
                      highlightedIdx === idx
                        ? toBlue(realisasiText)
                        : realisasiText,
                    deviasi: deviasiCell,
                  };
                });
                setWeeklyTable({
                  columns: [
                    { Header: "Minggu", accessor: "minggu", width: "20%" },
                    {
                      Header: "Tanggal (Awal - Akhir)",
                      accessor: "rentang",
                      width: "30%",
                    },
                    { Header: "Target (%)", accessor: "target", width: "16%" },
                    {
                      Header: "Realisasi (%)",
                      accessor: "realisasi",
                      width: "16%",
                    },
                    {
                      Header: "Deviasi (%)",
                      accessor: "deviasi",
                      width: "18%",
                    },
                  ],
                  rows: weeklyRows,
                });
              }
            })
            .catch((error) => {
              if (error.name !== "AbortError") {
                console.error("Error fetching realisasi data:", error);
              }
              setChartData({
                labels: labelGrap,
                datasets: [
                  {
                    label: "Akumulasi Target (%)",
                    color: "info",
                    data: totPro,
                  },
                ],
              });
            })
            .finally(() => {
              setLoadingChart(false);
            });
        })
        .catch((error) => {
          if (error.name !== "AbortError") {
            console.error("Error fetching kurva S data:", error);
          }
          setChartData(null);
          setLoadingChart(false);
        });
    } else {
      setLoadingChart(false);
    }

    // Cleanup function
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [
    id,
    tahun,
    selectedProyek?.tanggal_mulai,
    selectedProyek?.tanggal_selesai,
  ]);

  // useLayoutEffect for immediate state reset before render
  useLayoutEffect(() => {
    if (id) {
      setChartData(null);
      setLoadingChart(true);
    }
  }, [id]);

  // Fungsi untuk kembali ke halaman sebelumnya
  const handleBack = () => {
    navigate(-1); // Kembali ke halaman sebelumnya
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox my={3}>
        {/* Tombol Back */}
        <MDBox mb={2}>
          <MDButton
            variant="outlined"
            color="info"
            onClick={handleBack}
            startIcon={<ArrowBackIcon />}
            sx={{
              borderColor: "#1976d2",
              color: "#1976d2",
              "&:hover": {
                borderColor: "#1565c0",
                backgroundColor: "rgba(25, 118, 210, 0.04)",
              },
            }}
          >
            Kembali
          </MDButton>
        </MDBox>

        {selectedProyek && (
          <MDBox mt={1} p={3} borderRadius={2} bgcolor="#f0f4fa" boxShadow={2}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={6}>
                <MDTypography
                  variant="h6"
                  fontWeight="bold"
                  color="info.main"
                  mb={1}
                >
                  {selectedProyek.label}
                </MDTypography>
                <MDTypography variant="body2" color="text">
                  <b>Lokasi:</b> {selectedProyek.lokasi || "-"}
                </MDTypography>
                <MDTypography variant="body2" color="text">
                  <b>Desa:</b> {selectedProyek.desa || "-"}
                </MDTypography>
                <MDTypography variant="body2" color="text">
                  <b>Koordinat:</b>{" "}
                  {selectedProyek.latitude && selectedProyek.longitude
                    ? `${selectedProyek.latitude}, ${selectedProyek.longitude}`
                    : "-"}
                </MDTypography>
              </Grid>
              <Grid item xs={12} md={6}>
                <MDTypography variant="body2" color="text">
                  <b>Tanggal Mulai:</b>{" "}
                  {formatTanggalIndo(selectedProyek.tanggal_mulai)}
                </MDTypography>
                <MDTypography variant="body2" color="text">
                  <b>Tanggal Selesai:</b>{" "}
                  {formatTanggalIndo(selectedProyek.tanggal_selesai)}
                </MDTypography>
                <MDTypography variant="body2" color="text">
                  <b>Konsultan Perencana:</b>{" "}
                  {selectedProyek.nama_konsultan_perencana ||
                    selectedProyek.konsultan_perencana?.nama ||
                    "-"}
                </MDTypography>
                <MDTypography variant="body2" color="text">
                  <b>Kontraktor Pelaksana:</b>{" "}
                  {selectedProyek.nama_kontraktor_pelaksana ||
                    selectedProyek.kontraktor_pelaksana?.nama ||
                    "-"}
                </MDTypography>
                <MDTypography variant="body2" color="text">
                  <b>Konsultan Pengawas:</b>{" "}
                  {selectedProyek.nama_konsultan_pengawas ||
                    selectedProyek.konsultan_pengawas?.nama ||
                    "-"}
                </MDTypography>
              </Grid>
              {loadingChart ? (
                <MDBox
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                  py={4}
                >
                  <CircularProgress color="info" />
                  <MDTypography variant="body1" ml={2} color="text.secondary">
                    Memuat data chart...
                  </MDTypography>
                </MDBox>
              ) : !chartData ? (
                <MDBox
                  display="flex"
                  flexDirection="column"
                  justifyContent="center"
                  alignItems="center"
                  py={6}
                  textAlign="center"
                >
                  <MDTypography variant="h6" color="text.secondary" mb={1}>
                    Tidak ada data chart
                  </MDTypography>
                  <MDTypography variant="body2" color="text.secondary">
                    Belum ada data realisasi mingguan untuk proyek ini pada
                    tahun {tahun}
                  </MDTypography>
                </MDBox>
              ) : (
                <Grid
                  container
                  spacing={3}
                  key={`chart-${selectedProyek?.value}-${tahun}-${
                    chartData ? "has-data" : "no-data"
                  }`}
                >
                  <Grid item xs={12}>
                    <GradientLineChart
                      icon={{ component: "show_chart" }}
                      title="Akumulasi Target vs Realisasi Mingguan"
                      height="20rem"
                      description="Akumulasi target dan realisasi seluruh pekerjaan utama per minggu"
                      chart={chartData}
                    />
                  </Grid>
                </Grid>
              )}
            </Grid>
          </MDBox>
        )}
        <MDBox mb={6}>
          {weeklyTable?.rows?.length > 0 && (
            <Grid item xs={12}>
              <Card>
                <MDBox p={3} lineHeight={1}>
                  <MDTypography variant="h6" fontWeight="medium">
                    Tabel Target & Realisasi Mingguan (beserta Deviasi)
                  </MDTypography>
                  <MDTypography variant="button" color="text">
                    Proyek {selectedProyek?.label} - Tahun {tahun}
                  </MDTypography>
                </MDBox>
                <DataTable
                  table={{
                    columns: weeklyTable.columns,
                    rows: weeklyTable.rows,
                  }}
                  entriesPerPage={{ defaultValue: 100, values: [100] }}
                />
              </Card>
            </Grid>
          )}
        </MDBox>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

export default KurvaTargetRealisasiProyek;
